import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { EditField } from "./EditField";
import { borders } from "../../design-system/tokens";
import { sortFieldsByAppearanceOrder, shouldShowField } from "../utils/grouping";
import { computePanelSpaceCapacity, packFieldsIntoRows } from "../utils/fieldWidths";

/**
 * EditFieldGrid
 *
 * Lays out a list of edit fields on a "width spaces" track. The grid measures
 * its own pixel width to derive a capacity (spaces per line), then renders each
 * visible field across the number of spaces it requests (see
 * `editor/utils/fieldWidths.js`). Compact controls (toggles, colours, sliders,
 * two-option choices, …) pack onto the same line on wider panels, while rich
 * editors keep a whole line to themselves. On a narrow panel the capacity
 * collapses to 1, so every field sits alone on its own line.
 *
 * Visibility (`shouldShowField`) and appearance-order sorting are handled here
 * so every call site shares identical behaviour.
 *
 * @param {Array}    fields          - [{ field, metadata }] to render.
 * @param {Object}   values          - Current element values (used for value +
 *                                      show_if/hide_if evaluation).
 * @param {Function} onChange        - (field, value) change handler.
 * @param {string}   [parentFieldPath] - Forwarded to EditField for nested fields.
 * @param {string}   [className]     - Extra classes for the grid wrapper.
 * @param {React.ReactNode} [emptyState] - Rendered when no field is visible.
 */
const EditFieldGrid = ({
  fields,
  values,
  onChange,
  parentFieldPath,
  className = "",
  emptyState = null,
}) => {
  const containerRef = useRef(null);
  const [capacity, setCapacity] = useState(1);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const applyWidth = (width) => {
      const next = computePanelSpaceCapacity(width);
      setCapacity((prev) => (prev === next ? prev : next));
    };

    try {
      applyWidth(el.getBoundingClientRect().width);
    } catch (_) {
      /* getBoundingClientRect unavailable — fall back to capacity 1 */
    }

    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) applyWidth(rect.width);
    });
    observer.observe(el);
    return () => {
      try {
        observer.disconnect();
      } catch (_) {
        /* no-op */
      }
    };
  }, []);

  const visibleFields = useMemo(
    () =>
      sortFieldsByAppearanceOrder(fields || []).filter(({ metadata }) =>
        shouldShowField(metadata, values),
      ),
    [fields, values],
  );

  const rows = useMemo(
    () => packFieldsIntoRows(visibleFields, capacity),
    [visibleFields, capacity],
  );

  const wrapperClassName = className.trim();

  // Always render the (measuring) container so width stays known even when
  // fields are temporarily hidden and reappear (e.g. via show_if conditions).
  // Each row is its own grid sized to that row's *total* span (not the panel
  // capacity), so the fields on a partially-filled row stretch to fill the full
  // width proportionally — e.g. three 1-space fields on a 4-space panel each
  // grow to a third of the row instead of leaving a trailing gap.
  return (
    <div ref={containerRef} className={wrapperClassName}>
      {visibleFields.length === 0
        ? emptyState
        : rows.map(({ cells, total }, rowIndex) => {
            // A field "shares" its row only when packed next to siblings; a lone
            // field stretches to the full width and keeps its default layout.
            const sharesRow = cells.length > 1;
            return (
              <div
                key={cells.map((cell) => cell.field).join("|") || rowIndex}
                className="grid gap-x-4"
                style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}
              >
                {cells.map(({ field, metadata, span }) => (
                  <div
                    key={field}
                    data-edit-field={field}
                    className={`min-w-0 mb-2 pb-2 border-b ${borders.light}`}
                    style={{ gridColumn: `span ${span} / span ${span}` }}
                  >
                    <EditField
                      field={field}
                      value={values?.[field]}
                      metadata={metadata}
                      allValues={values}
                      onChange={onChange}
                      parentFieldPath={parentFieldPath}
                      sharesRow={sharesRow}
                    />
                  </div>
                ))}
              </div>
            );
          })}
    </div>
  );
};

export { EditFieldGrid };
